// Types
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import HistoricalScenario from '@/types/historical-scenario';
import ProposalMetadata from '@/types/proposal-metadata';
import ReducedComparison from '@/types/reduced-comparison';
import User from '@/types/user';
import { AIStatementReaderStatus } from '@/redux/modules/types/ai-statement-reader';
import { OmniRiskProfileConfig } from '@/types/models/risk-profiles/omni-risk-profiles-config';

// Utils
import createBatchLogger from '@/utils/batch-logger';
import disclaimersUtils from '@/utils/disclaimers';
import PortfolioHolding from '@/types/portfolio-holding';
import Timestamp from '@/types/timestamp';
import { correlateApi } from '@/utils/api';
import { getTimeElapsed } from '@/utils/time';
import { LOCALE } from '@/utils/i18n';
import { USAGE_TYPES, serializeComparisonItem } from '@/utils/usage-log';
import { isUpRisk } from '@/utils/risk-profiles';
import { riskProfileUpRisk } from '@/types/risk-profile-item';
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { decodeIdForExistingClientPortfolio } from '@/utils/portfolios';

const apiPath = '/usage-logs';
const postBatch = (logs: { type: string; values: any }[]) => correlateApi.post(`${apiPath}/`, { logs });

const sendUsageLog = createBatchLogger(postBatch);

export const addUsageLog = (type: string, values: any) => {
  sendUsageLog({ type, values });
};

export const logAttachOmniRiskProfileToComparison = (comparison: Comparison, profile: Partial<OmniRiskProfileConfig>) => {
  let comparisonId = comparison._id;
  if (comparisonId && comparisonId.startsWith('new-')) comparisonId = undefined;
  const values = {
    comparisonId,
    comparisonCorrelationId: comparison.correlationId,
    omniRiskProfileId: profile._id,
    omniRiskProfileTitle: profile.title && profile.title[LOCALE.EN_CA],
  };

  addUsageLog(USAGE_TYPES.ATTACH_OMNI_RISK_PROFILE_TO_COMPARISON, values);
};

export const logCancelAIStatementReaderProcess = (files: File[], start?: Timestamp) => {
  const timeElapsed = getTimeElapsed(start);

  // TODO: Update log when multiple files will be uploaded
  // Only sending one file for now
  const formData = new FormData();
  formData.append('file', files[0]);
  const uploadedFile = formData.get('file') as File;

  const values = {
    stopProcessReason: AIStatementReaderStatus.CANCELLED,
    // Time taken in milliseconds since begin the processing until cancel the process
    ...(timeElapsed ? { timeTakenInMsToCancelTheProcess: timeElapsed } : {}),
    ...(uploadedFile && uploadedFile.size && uploadedFile.type
      ? {
          uploadedFileFormat: uploadedFile.type,
          uploadedFileSize: uploadedFile.size,
        }
      : {}),
  };

  addUsageLog(USAGE_TYPES.CANCEL_AI_STATEMENT_READER_PROCESS, values);
};

export const logDismissUnlockAIStatementReaderFromMarketingBanner = () => {
  addUsageLog(USAGE_TYPES.DISMISS_UNLOCK_AI_STATEMENT_READER_FROM_MARKETING_BANNER, {});
};

export const logDisplayAIStatementReaderMarketingBanner = () => {
  addUsageLog(USAGE_TYPES.DISPLAY_AI_STATEMENT_READER_MARKETING_BANNER, {});
};

export const logDocumentsProcessedByAIStatementReader = (
  formData: FormData,
  holdings: PortfolioHolding[],
  aiStatementReadEndTime?: Timestamp,
  aiStatementReadStartTime?: Timestamp,
) => {
  // TODO: Update log when multiple files will be uploaded
  const uploadedFile = formData.get('file') as File;
  if (!uploadedFile) return;

  const missingInstruments = holdings.filter((h: PortfolioHolding) => disclaimersUtils.holdingIsMissing(h));

  // Define measurement based in state timestamps
  const timeToReadFiles = aiStatementReadEndTime && aiStatementReadStartTime && getTimeElapsed(aiStatementReadStartTime, aiStatementReadEndTime);

  const values = {
    missingInstruments,
    // Time taken in milliseconds since begin the processing until the end of processing
    ...(timeToReadFiles ? { timeTakenInMsToExtractAndAnalyzeData: timeToReadFiles } : {}),
    totalInstruments: holdings.length,
    totalMissingInstruments: missingInstruments.length,
    uploadedFileFormat: uploadedFile.type,
    uploadedFileSize: uploadedFile.size,
    statusCode: 200,
    statusText: AIStatementReaderStatus.SUCCESS,
  };

  addUsageLog(USAGE_TYPES.DOCUMENTS_PROCESSED_BY_AI_STATEMENT_READER, values);
};

export type LogDownloadProposalParams = {
  items: ComparisonItem[];
  sections: string[];
  metadata: ProposalMetadata;
  riskProfileUpRisk?: riskProfileUpRisk;
  proposalName: string;
};

export const logDownloadProposal = ({ items, sections, metadata, riskProfileUpRisk, proposalName }: LogDownloadProposalParams) => {
  const { advisor, locale, template, missingHoldingsProxyLog, comparisonId, nameId, contactId } = metadata;

  // Extract account IDs from items, filtering out non-portfolio items
  const accountIds = items.map(item => (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO ? decodeIdForExistingClientPortfolio(item._id) : undefined));

  const values = {
    advisor,
    instruments: items.map(serializeComparisonItem),
    isUpRisk: isUpRisk(riskProfileUpRisk),
    locale,
    sections,
    template,
    missingHoldingsProxyLog,
    proposalName,
    comparisonId,
    nameId,
    accountIds,
    contactId,
  };

  addUsageLog(USAGE_TYPES.DOWNLOAD_PROPOSAL, values);
};

export const logErrorInAIStatementReader = (error: any, start: Timestamp) => {
  const timeElapsed = getTimeElapsed(start);

  // TODO: Update log when multiple files will be available
  const uploadedFile = error?.config?.data?.get('file') as File;
  if (!uploadedFile) return;

  const values = {
    error: error.data.error,
    errorKey: error.data.errorKey,
    errorStatusCode: error.status,
    statusText: AIStatementReaderStatus.FAILED,
    timeTakenInMsInTheRequest: timeElapsed,
    uploadedFileFormat: uploadedFile.type,
    uploadedFileSize: uploadedFile.size,
  };

  addUsageLog(USAGE_TYPES.ERROR_IN_AI_STATEMENT_READER, values);
};

export const logComparisonMarkSharedWithClient = (comparison: ReducedComparison, user: User) => {
  let comparisonId = comparison._id;
  if (comparisonId && comparisonId.startsWith('new-')) comparisonId = undefined;

  const values = {
    userNameId: user.nameId,
    email: user.email,
    comparisonId,
    contactId: comparison.contact?._id,
  };

  addUsageLog(USAGE_TYPES.COMPARISON_MARK_SHARED_WITH_CLIENT, values);
};

export const logHistoricalScenariosUsage = (time: Timestamp, presentationType?: string, scenarios?: HistoricalScenario[]) => {
  const values = {
    presentationType,
    scenariosAddedToComparison:
      scenarios?.map(scenario => ({
        endDate: scenario.endDate,
        scenarioName: scenario.name[LOCALE.EN_CA],
        scenarioType: scenario?.ownerId ? 'Custom' : 'Predefined',
        startDate: scenario.startDate,
      })) ?? [],
    // Duration (ms) using the Historical Scenarios until presentation export
    timeTakenInMsReviewingHistoricalScenarios: time,
  };

  addUsageLog(USAGE_TYPES.HISTORICAL_SCENARIOS_USAGE, values);
};

export const logSaveAIStatementPortfolio = (start: Timestamp) => {
  const timeElapsed = getTimeElapsed(start);

  // TODO: CONN-8067 will have additional information to this log.
  const values = {
    // Time taken in milliseconds since end the processing until save the portfolio
    timeTakenInMsReviewingStatements: timeElapsed,
  };

  addUsageLog(USAGE_TYPES.SAVE_AI_STATEMENT_PORTFOLIO, values);
};

export const logUnlockAIStatementReaderFromMarketingBanner = () => {
  addUsageLog(USAGE_TYPES.UNLOCK_AI_STATEMENT_READER_FROM_MARKETING_BANNER, {});
};

export default {
  addUsageLog,
  logAttachOmniRiskProfileToComparison,
  logCancelAIStatementReaderProcess,
  logComparisonMarkSharedWithClient,
  logDismissUnlockAIStatementReaderFromMarketingBanner,
  logDisplayAIStatementReaderMarketingBanner,
  logDocumentsProcessedByAIStatementReader,
  logDownloadProposal,
  logErrorInAIStatementReader,
  logHistoricalScenariosUsage,
  logSaveAIStatementPortfolio,
  logUnlockAIStatementReaderFromMarketingBanner,
};
